import { FileSpreadsheet, X } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { ParsedDopplerUri } from "../utils/dopplerUri";
import SpreadsheetView from "./SpreadsheetView";

/** A cached workbook resource. ``value`` is populated once ``promise``
 * resolves, letting callers read a hit synchronously (no loading flash). */
export interface WorkbookCacheEntry {
	promise: Promise<XLSX.WorkBook>;
	value?: XLSX.WorkBook;
	/** Server-provided filename (from the download response), when resolved. */
	filename?: string;
}

/** Resolve+fetch+parse a workbook, deduplicated/cached by file identity.
 * Owned by the provider so it survives modal open/close. */
export type LoadWorkbook = (
	parsed: ParsedDopplerUri,
	chatId?: string | null,
) => WorkbookCacheEntry;

interface SpreadsheetViewerProps {
	parsed: ParsedDopplerUri;
	chatId?: string | null;
	/** Display name in the header; falls back to a generic label. */
	filename?: string;
	/** Cache-backed loader; load is keyed on file identity, not view state. */
	loadWorkbook: LoadWorkbook;
	onClose: () => void;
}

/**
 * The modal shell around `SpreadsheetView`.
 *
 * Everything about rendering the workbook — sheet tabs, grid, the highlighted
 * cited cell — lives in that component, so the embedded views (the KPI
 * drill-in) show exactly what this modal shows. What is left here is the
 * framing an overlay needs: the backdrop, the header, and Escape-to-close.
 */
const SpreadsheetViewer: React.FC<SpreadsheetViewerProps> = ({
	parsed,
	chatId,
	filename,
	loadWorkbook,
	onClose,
}) => {
	// Filename resolved from the download response, used when the citation that
	// opened the viewer carried none.
	const [resolvedFilename, setResolvedFilename] = useState<string | null>(null);
	const [activeSheet, setActiveSheet] = useState<string | null>(null);

	const handleResolvedFilename = useCallback(
		(name: string | null) => setResolvedFilename(name),
		[],
	);

	// Close on Escape.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);

	const focusLabel = parsed.range ?? parsed.cell ?? null;

	return createPortal(
		<div
			className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
			onClick={onClose}
		>
			<div
				className="relative flex max-h-[88vh] w-full max-w-6xl flex-col rounded-lg bg-white shadow-xl"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-3">
					<div className="flex min-w-0 items-center gap-2">
						<FileSpreadsheet className="h-5 w-5 shrink-0 text-teal-600" />
						<div className="min-w-0">
							<div className="truncate text-sm font-medium text-gray-900">
								{filename ?? resolvedFilename ?? "Spreadsheet"}
							</div>
							{focusLabel && activeSheet && (
								<div className="truncate text-xs text-gray-500">
									{activeSheet}!{focusLabel}
								</div>
							)}
						</div>
					</div>
					<button
						onClick={onClose}
						className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
						aria-label="Close"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<SpreadsheetView
					parsed={parsed}
					chatId={chatId}
					loadWorkbook={loadWorkbook}
					onResolvedFilename={handleResolvedFilename}
					onActiveSheet={setActiveSheet}
				/>
			</div>
		</div>,
		document.body,
	);
};

export default SpreadsheetViewer;
